import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControlLabel,
  FormHelperText,
  Radio,
  Dialog,
  DialogContent,
} from "@material-ui/core";
import CommonButton from "../button";
import { makeStyles } from "@material-ui/core/styles";
import EmailIcon from "@material-ui/icons/Email";
import SendIcon from "@material-ui/icons/Send";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import theme from "../../theme";

const useStyles = makeStyles(() => ({
  root: {
    background: theme.colors.White,
    borderRadius: 0, // dialog handles the radius
    padding: "28px 32px",
    overflowY: "auto",
    maxHeight: "90vh",  // cap it so it never overflows the viewport
  },
  header: { marginBottom: 24 },
  title: {
    fontSize: 17,
    fontFamily: theme.fontFamily.MARKPROBLACK,
    color: theme.colors.Violet,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: 13,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: "#8892a4",
    marginTop: 4,
  },

  // Email field
  fieldLabel: {
    fontSize: 11,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: "#8892a4",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    display: "block",
    marginBottom: 6,
  },
  emailInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 13.5,
      fontFamily: theme.fontFamily.MARKPROBOOK,
      backgroundColor: "#FAFCFF",
      "& fieldset": { borderColor: theme.colors.Gallery, borderWidth: 1.5 },
      "&:hover fieldset": { borderColor: theme.colors.Periwinkle },
      "&.Mui-focused fieldset": { borderColor: theme.colors.DodgerBlue, borderWidth: 1.5 },
      "&.Mui-error fieldset": { borderColor: theme.colors.Monza },
    },
    "& .MuiOutlinedInput-adornedStart": { paddingLeft: 12 },
  },
  emailAdornment: {
    color: "#C5DAFE",
    fontSize: 18,
    marginRight: 6,
    display: "flex",
  },

  // Permission section
  permissionSection: {
    marginTop: 20,
    background: "#FAFCFF",
    border: `1.5px solid ${theme.colors.Zumthor}`,
    borderRadius: 12,
    padding: "14px 16px",
  },
  permissionTitle: {
    fontSize: 11,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: "#8892a4",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 12,
    display: "block",
  },
  permissionOptions: { display: "flex", flexDirection: "column", gap: 8 },
  permissionRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 10,
    border: "1.5px solid transparent",
    cursor: "pointer",
    transition: "all 0.15s",
  },
  permissionRowActive: {
    border: `1.5px solid ${theme.colors.Periwinkle}`,
    background: "#ECF3FF",
  },
  permissionRowInactive: {
    border: `1.5px solid ${theme.colors.Gallery}`,
    background: theme.colors.White,
    "&:hover": { borderColor: theme.colors.Periwinkle, background: "#F8FAFF" },
  },
  checkbox: {
    padding: 0,
    color: theme.colors.DodgerBlue,
    "&.Mui-checked": { color: theme.colors.DodgerBlue },
  },
  radioBtn: {
    padding: 0,
    color: theme.colors.DodgerBlue,
    "&.Mui-checked": { color: theme.colors.DodgerBlue },
  },
  permissionLabel: {
    fontSize: 13.5,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    color: theme.colors.Violet,
    lineHeight: 1.3,
  },
  permissionDesc: {
    fontSize: 11.5,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    color: "#8892a4",
    marginTop: 2,
    lineHeight: 1.4,
  },
  permissionError: {
    color: theme.colors.Monza,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 12,
    marginTop: 6,
    marginLeft: 2,
  },

  // Error helper
  helperError: {
    color: theme.colors.Monza,
    fontFamily: theme.fontFamily.MARKPROBOOK,
    fontSize: 12,
    marginTop: 4,
    marginLeft: 2,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },

  // Footer
  footer: { marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 10 },
  btnCancel: {
    background: theme.colors.Porcelain,
    color: "#8892a4",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 13,
    textTransform: "none",
    borderRadius: 10,
    padding: "9px 20px",
    border: `1.5px solid ${theme.colors.Gallery}`,
    "&:hover": { background: theme.colors.Gallery, color: theme.colors.Violet },
  },
  btnInvite: {
    background: theme.colors.DodgerBlue,
    color: theme.colors.White,
    fontFamily: theme.fontFamily.MARKPROBOLD,
    fontSize: 13,
    textTransform: "none",
    borderRadius: 10,
    padding: "9px 20px",
    boxShadow: "0 2px 10px rgba(62,131,251,0.25)",
    "&:hover": { background: "#2A6FE6", boxShadow: "0 4px 14px rgba(62,131,251,0.35)" },
    "&:disabled": { background: theme.colors.Gallery, color: "#8892a4", boxShadow: "none" },
  },
  
  dialogPaper: {
    borderRadius: 20,
    boxShadow: "0 16px 48px rgba(21,14,74,0.16)",
    maxWidth: 480,
    width: "100%",
    margin: 16,
    overflow: "hidden", // prevents the content from bleeding out
  },
  dialogContent: {
    padding: 0,
    overflow: "hidden", // kills the internal scrollbar
    "&:first-child": { paddingTop: 0 },
  },
  // Add a close button to the card header
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  closeBtn: {
    width: 32,
    height: 32,
    border: `1.5px solid ${theme.colors.Gallery}`,
    background: theme.colors.Porcelain,
    color: "#8892a4",
    flexShrink: 0,
    "&:hover": {
      background: theme.colors.Cinderella,
      borderColor: theme.colors.RadicalRed,
      color: theme.colors.Monza,
    },
  },
}));

// ─── Permission options config ────────────────────────────────────────────────
const PERMISSIONS = [
  {
    value: "HR",
    label: "HR Permission",
    description: "Can manage employee records, onboarding, and employment applications.",
  },
  {
    value: "ADMIN",
    label: "Admin Permission",
    description: "Full access — manage users, settings, permissions, and all HR functions.",
  },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INITIAL_STATE = { email: "", permissions: [] };
const INITIAL_ERRORS = { email: "", permissions: "" };

const validate = (data) => {
  const next = { email: "", permissions: "" };

  if (!data.email.trim()) {
    next.email = "Email address is required.";
  } else if (!EMAIL_REGEX.test(data.email.trim())) {
    next.email = "Please enter a valid email address.";
  }

  if (data.permissions.length === 0) {
    next.permissions = "Select at least one permission role.";
  }

  return next;
};

const hasErrors = (errs) => Object.values(errs).some(Boolean);

/**
 * InviteAdminModal
 *
 * Props:
 *   open     {boolean}
 *   onClose  {() => void}
 *   onInvite {({ email: string, permission: string }) => void}
 */
export default function InviteAdminModal({ open, onClose, onInvite = () => { } }) {
  const classes = useStyles();
  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [submitted, setSubmitted] = useState(false);

  const handleClose = () => {
    setForm(INITIAL_STATE);
    setErrors(INITIAL_ERRORS);
    setSubmitted(false);
    onClose();
  };

  // ── same handlers as before ───────────────────────────────────────────────
  const handleEmailChange = (e) => {
    const email = e.target.value;
    setForm((prev) => ({ ...prev, email }));
    if (submitted) {
      setErrors((prev) => ({
        ...prev,
        email: !email.trim()
          ? "Email address is required."
          : !EMAIL_REGEX.test(email.trim())
            ? "Please enter a valid email address."
            : "",
      }));
    }
  };

  const handlePermissionToggle = (value) => {
    setForm((prev) => {
      const permissions = prev.permissions[0] === value ? [] : [value];
      if (submitted) {
        setErrors((e) => ({
          ...e,
          permissions: permissions.length === 0 ? "Select at least one permission role." : "",
        }));
      }
      return { ...prev, permissions };
    });
  };

  const handleSubmit = () => {
    setSubmitted(true);
    const errs = validate(form);
    setErrors(errs);
    if (hasErrors(errs)) return;

    onInvite({ email: form.email.trim(), permission: form.permissions[0] });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
      // reset form state when clicking backdrop
      disableBackdropClick={false}
    >
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.root}>
          {/* Header */}
          <Box className={classes.headerRow}>
            <Box>
              <Typography className={classes.title}>Invite Administrator</Typography>
              <Typography className={classes.subtitle}>
                Send an invitation to grant access and set their permission level.
              </Typography>
            </Box>
            <IconButton className={classes.closeBtn} size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Email field */}
          <Box>
            <span className={classes.fieldLabel}>Email Address</span>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="e.g. admin@company.ng"
              value={form.email}
              onChange={handleEmailChange}
              error={!!errors.email}
              className={classes.emailInput}
              InputProps={{
                startAdornment: (
                  <Box className={classes.emailAdornment}>
                    <EmailIcon fontSize="inherit" />
                  </Box>
                ),
              }}
            />
            {errors.email && (
              <FormHelperText className={classes.helperError}>
                {errors.email}
              </FormHelperText>
            )}
          </Box>

          {/* Permission selection */}
          <Box className={classes.permissionSection}>
            <span className={classes.permissionTitle}>Permission Role</span>
            <Box className={classes.permissionOptions}>
              {PERMISSIONS.map((perm) => {
                const isChecked = form.permissions[0] === perm.value;
                return (
                  <Box
                    key={perm.value}
                    className={`${classes.permissionRow} ${isChecked ? classes.permissionRowActive : classes.permissionRowInactive}`}
                    onClick={() => handlePermissionToggle(perm.value)}
                  >
                    <Radio
                      checked={isChecked}
                      className={classes.radioBtn}
                      size="small"
                      onChange={() => handlePermissionToggle(perm.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Box>
                      <Typography className={classes.permissionLabel}>{perm.label}</Typography>
                      <Typography className={classes.permissionDesc}>{perm.description}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {errors.permissions && (
              <FormHelperText className={classes.permissionError}>
                {errors.permissions}
              </FormHelperText>
            )}
          </Box>

          {/* Footer */}
          <Box className={classes.footer}>
            <Button className={classes.btnCancel} onClick={handleClose}>
              Cancel
            </Button>
            <CommonButton
              className={classes.btnInvite}
              startIcon={<SendIcon fontSize="small" />}
              onClick={handleSubmit}
              text="Send Invite"
              disabled={hasErrors(errors) || !form.email.trim() }
            />
              
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}